import React, { useState } from "react";
import Icon from "@mdi/react";
import { mdiLinkVariant, mdiOpenInNew, mdiCheckCircle } from "@mdi/js";
import { CircularProgress } from "@mui/material";
import { clsx } from "@ismael1361/utils";
import { McpLibraryServices, NotistackServices } from "shared/services";
import styles from "./styles.module.scss";

export interface OAuthPendingItem {
	slug: string;
	displayName: string;
	contractedMcpId?: string | null;
	envName?: string | null;
}

export interface OAuthActionMessageProps {
	memberName?: string;
	teamName?: string;
	items: OAuthPendingItem[];
	disabled?: boolean;
	/** Marca itens já conectados (após retorno do OAuth ou refresh manual) */
	connectedSlugs?: string[];
}

/**
 * Card de ação inline no chat para conectar contas OAuth (Outlook / Outlook Calendar / OneDrive / Teams etc).
 *
 * Disparado quando uma tool retorna `error: "OAUTH_CONNECTION_REQUIRED"` com
 * a lista `pendingOAuth`. O usuário pode clicar para iniciar o fluxo OAuth2
 * direto do chat, sem precisar navegar até a página de Ferramentas.
 */
export const OAuthActionMessage: React.FC<OAuthActionMessageProps> = ({ memberName, teamName, items, disabled, connectedSlugs }) => {
	const [loadingSlug, setLoadingSlug] = useState<string | null>(null);
	const connectedSet = new Set(connectedSlugs ?? []);

	const handleConnect = async (item: OAuthPendingItem) => {
		// Sem contract id: redireciona para o catálogo (usuário precisa contratar manualmente).
		if (!item.contractedMcpId) {
			window.location.assign(`/tools/mcp/${encodeURIComponent(item.slug)}`);
			return;
		}

		setLoadingSlug(item.slug);
		try {
			const callbackUrl = window.location.href;
			const response = await McpLibraryServices.initHiredMcpOAuth2(item.contractedMcpId, {
...(item.envName ? { envName: item.envName } : {}),
callbackUrl,
});

			if (!response.redirectUrl) {
				throw new Error("URL de autorização OAuth2 não foi retornada");
			}

			window.location.assign(response.redirectUrl);
		} catch (error: any) {
			setLoadingSlug(null);
			NotistackServices.showErrorMessage(error?.message ?? "Erro ao iniciar conexão OAuth");
		}
	};

	const headline =
		memberName && teamName
			? `O membro "${memberName}" da equipe "${teamName}" precisa conectar:`
			: "Para executar esta ação, conecte sua conta nestas ferramentas:";

	return (
<div className={styles.oauthActionCard}>
			<div className={styles.oauthActionHeader}>
				<Icon
					path={mdiLinkVariant}
					size={0.65}
					className={styles.oauthActionIcon}
				/>
				<span className={styles.oauthActionLabel}>CONEXÃO NECESSÁRIA</span>
			</div>
			<p className={styles.oauthActionTitle}>Autorização pendente</p>
			<p className={styles.oauthActionDescription}>{headline}</p>
			<div className={styles.oauthActionList}>
				{items.map((item) => {
					const isLoading = loadingSlug === item.slug;
					const isConnected = connectedSet.has(item.slug);
					return (
<button
							key={item.slug}
							type="button"
							className={clsx(styles.oauthActionBtn, isConnected && styles.oauthActionBtnConnected)}
							onClick={() => handleConnect(item)}
							disabled={disabled || isLoading || isConnected || !!loadingSlug}
						>
							{isConnected ? (
<>
									<Icon
										path={mdiCheckCircle}
										size={0.6}
									/>
									{item.displayName} conectado
								</>
							) : isLoading ? (
<>
									<CircularProgress
										size={12}
										color="inherit"
									/>
									Abrindo autorização…
								</>
							) : (
<>
									<Icon
										path={mdiOpenInNew}
										size={0.55}
									/>
									Conectar {item.displayName}
								</>
							)}
						</button>
					);
				})}
			</div>
			<p className={styles.oauthActionFootnote}>
				Você será redirecionado para a página oficial do provedor para autorizar o e-Keeps. Depois de autorizar, volte ao chat e peça a tarefa novamente.
			</p>
		</div>
	);
};
